import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  Upload,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useLanguage } from "../contexts/LanguageContext";
import { publicAPI } from "../api/publicAPI";
import { 
  validateName, 
  validatePhoneNumber, 
  validateMessage, 
  validateEmail,
  getCountryCodes 
} from "../utils/formValidation";

const Contact = () => {
  const { toast } = useToast();
  const { t, currentLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "",
    phone: "",
    message: "",
    file: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryCodes = getCountryCodes();

  const validateField = (name, value) => {
    let validation;
    
    switch(name) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhoneNumber(value, (formData.countryCode || '').split('__')[0]);
        break;
      case 'message':
        validation = validateMessage(value);
        break;
      default:
        return { isValid: true };
    }
    
    return validation;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value) return; // Don't validate empty fields on blur
    
    const validation = validateField(name, value);
    if (!validation.isValid) {
      setErrors({
        ...errors,
        [name]: validation.error
      });
    }
  };

  const handleCountryCodeChange = (code) => {
    setFormData({
      ...formData,
      countryCode: code
    });
    // Revalidate phone if already entered
    if (formData.phone) {
      const validation = validatePhoneNumber(formData.phone, code);
      if (!validation.isValid) {
        setErrors({
          ...errors,
          phone: validation.error
        });
      } else {
        setErrors({
          ...errors,
          phone: null
        });
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 6 * 1024 * 1024) { // 6MB limit
      setFormData({ ...formData, file });
    } else {
      toast({
        title: t('error'),
        description: currentLanguage === "RU" 
          ? "Файл слишком большой. Максимальный размер 6 МБ." 
          : "File too large. Maximum size is 6 MB.",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {};
    const nameValidation = validateName(formData.name);
    const emailValidation = validateEmail(formData.email);
    const phoneValidation = validatePhoneNumber(formData.phone, (formData.countryCode || '').split('__')[0]);
    const messageValidation = validateMessage(formData.message);
    
    if (!nameValidation.isValid) newErrors.name = nameValidation.error;
    if (!emailValidation.isValid) newErrors.email = emailValidation.error;
    if (!phoneValidation.isValid) newErrors.phone = phoneValidation.error;
    if (!messageValidation.isValid) newErrors.message = messageValidation.error;
    
    // Check if file is attached (required)
    if (!formData.file) {
      newErrors.file = currentLanguage === "RU" 
        ? "Необходимо прикрепить файл"
        : "File attachment is required";
    }
    
    // If there are errors, show them and don't submit
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast({
        title: currentLanguage === "RU" ? "Ошибка валидации" : "Validation Error",
        description: currentLanguage === "RU" 
          ? "Пожалуйста, исправьте ошибки в форме"
          : "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare form data for API
      const submitData = new FormData();
      submitData.append('name', nameValidation.cleaned);
      submitData.append('email', formData.email);
      submitData.append('phone', phoneValidation.formatted);
      submitData.append('message', messageValidation.cleaned);
      submitData.append('language', currentLanguage);
      submitData.append('file', formData.file);

      // Submit to API
      await publicAPI.submitInquiry(submitData);

      toast({
        title: currentLanguage === "RU" ? "Заявка отправлена!" : "Request sent!",
        description: currentLanguage === "RU" 
          ? "Мы свяжемся с вами в ближайшее время для консультации."
          : "We will contact you soon for consultation.",
        duration: 5000
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        countryCode: "",
        phone: "",
        message: "",
        file: null
      });
      setErrors({});
      
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast({
        title: currentLanguage === "RU" ? "Ошибка" : "Error",
        description: currentLanguage === "RU"
          ? "Не удалось отправить заявку. Пожалуйста, попробуйте позже."
          : "Failed to submit request. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {t('contactUs')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('contactDescription')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Contact info */}
          <div className="lg:col-span-1 space-y-6 order-2 lg:order-1">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">
                  {t('contactInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Phone className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{t('phone')}</p>
                    <p className="text-gray-600">+7 (499) 284-29-88</p>
                    <p className="text-sm text-gray-500"></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Mail className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{t('email')}</p>
                    <p className="text-gray-600">mail@velox-hint.com</p>
                    <p className="text-sm text-gray-500"></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <MapPin className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{t('address')}</p>
                    <p className="text-gray-600">Москва, ул. Поречная, д. 5/14 стр 1</p>
                    <p className="text-sm text-gray-500"></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Режим работы</p>
                    <p className="text-gray-600">Пн-Вс: 09:00 - 18:00</p>
                    <p className="text-sm text-gray-500"></p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick benefits */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="p-6">
                <h3 className="font-semibold text-blue-900 mb-4">
                  {t('getRightNow')}
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-800">{t('freeConsultationItem')}</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-800">{t('documentAnalysis')}</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-800">{t('costCalculation')}</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-800">{t('actionPlan')}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">
                  {t('calculateServiceCost')}
                </CardTitle>
                <p className="text-gray-600">
                  Загрузите файл с описанием товара, укажите город для доставки и мы рассчитаем стоимость
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name field with validation */}
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('yourName')} *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        className={`border-gray-300 focus:border-orange-500 focus:ring-orange-500 ${
                          errors.name ? 'border-red-500 focus:border-red-500' : ''
                        }`}
                        placeholder={currentLanguage === "RU" ? "Иван Иванов" : "John Smith"}
                      />
                      {errors.name && (
                        <div className="flex items-center space-x-1 text-red-600 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          <span>{errors.name}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Email field with validation */}
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('email')} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        className={`border-gray-300 focus:border-orange-500 focus:ring-orange-500 ${
                          errors.email ? 'border-red-500 focus:border-red-500' : ''
                        }`}
                        placeholder="example@mail.com"
                      />
                      {errors.email && (
                        <div className="flex items-center space-x-1 text-red-600 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          <span>{errors.email}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Phone field with country code selector */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('phone')} *</Label>
                    <div className="flex space-x-2">
                      <Select value={formData.countryCode} onValueChange={handleCountryCodeChange}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder={currentLanguage === "RU" ? "Регион" : "Region"} />
                        </SelectTrigger>
                          <SelectContent>
                            {countryCodes.map((country) => (
                              <SelectItem key={`${country.code}-${country.country}`} value={`${country.code}__${country.country}`}>
                                {country.code} {country.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          className={`flex-1 border-gray-300 focus:border-orange-500 focus:ring-orange-500 ${
                            errors.phone ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                          placeholder={(formData.countryCode || '').split('__')[0] === "+7" ? "9001234567" : "1234567890"}
                        />
                      </div>
                      {errors.phone && (
                        <div className="flex items-center space-x-1 text-red-600 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          <span>{errors.phone}</span>
                        </div>
                      )}
                    </div>

                  {/* Message field with validation */}
                  <div className="space-y-2">
                    <Label htmlFor="message">{t('message')} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      className={`border-gray-300 focus:border-orange-500 focus:ring-orange-500 ${
                        errors.message ? 'border-red-500 focus:border-red-500' : ''
                      }`}
                      placeholder={currentLanguage === "RU" 
                        ? "Опишите ваш запрос или вопрос..." 
                        : "Describe your request or question..."}
                    />
                    {errors.message && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>{errors.message}</span>
                      </div>
                    )}
                  </div>

                  {/* File upload */}
                  <div className="space-y-2">
                    <Label htmlFor="file">{t('sendFile')} <span className="text-red-500">*</span></Label>
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-orange-400 transition-colors duration-200 ${
                      errors.file ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}>
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="file" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            {t('clickToUpload')}
                          </span>
                          <span className="mt-1 block text-xs text-gray-500">
                            {t('maxSize')}
                          </span>
                        </label>
                        <input
                          id="file"
                          name="file"
                          type="file"
                          className="sr-only"
                          onChange={handleFileChange}
                          accept=".doc,.docx,.pdf,.txt,.xlsx"
                          required
                        />
                      </div>
                      {formData.file && (
                        <p className="mt-2 text-sm text-orange-600 font-medium">
                          Выбран файл: {formData.file.name}
                        </p>
                      )}
                      {errors.file && (
                        <p className="mt-2 text-sm text-red-600 font-medium">
                          {errors.file}
                        </p>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-lg py-3"
                  >
                    {isSubmitting ? (
                      "Отправляем..."
                    ) : (
                      <>
                        {t('sendRequest')}
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    {t('agreeToProcessing')}
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
